/**
 * UserContextService — Cache léger du user pour accès synchrone
 *
 * Les services (matching, scoring, catalogue) ont besoin d'accéder à
 * certaines infos user (âge, genre, tier...) sans passer par les hooks
 * React (qui ne marchent que dans les components).
 *
 * Ce service stocke ces infos en mémoire après chaque mise à jour du user
 * dans AuthContext et les expose via des getters synchrones.
 */
import { computeAge, getAgeGroup } from '../utils/userDisplay';

let _cachedUser     = null;
let _cachedAge      = null;
let _cachedAgeGroup = null;

/**
 * Met à jour le cache (appelé depuis AuthContext quand user change).
 */
export function setCachedUser(user) {
  _cachedUser     = user || null;
  _cachedAge      = user ? computeAge(user) : null;
  _cachedAgeGroup = user ? getAgeGroup(user) : null;
}

export function getCachedUser() {
  return _cachedUser;
}

export function getCachedUserAge() {
  return _cachedAge;
}

export function getCachedUserAgeGroup() {
  return _cachedAgeGroup;
}

export function getCachedUserGender() {
  return _cachedUser?.gender || null;
}

/**
 * Renvoie la silhouette user (mince/athletique/normal/costaud/forme), null si absente.
 */
export function getCachedUserSilhouette() {
  return _cachedUser?.silhouette || null;
}

/**
 * Renvoie la taille user en cm, null si absente.
 */
export function getCachedUserHeight() {
  return _cachedUser?.height_cm || null;
}

/**
 * Renvoie le poids user en kg, null si absent.
 */
export function getCachedUserWeight() {
  return _cachedUser?.weight_kg || null;
}

/**
 * Renvoie les tailles vestimentaires user (objet { tops, bottoms, shoes, jacket }).
 */
export function getCachedUserSizes() {
  return _cachedUser?.clothing_sizes || {};
}

/**
 * Renvoie un objet "context" complet pour le matching IA.
 * Centralisé pour ne pas avoir à appeler 6 getters.
 */
export function getCachedUserContext() {
  return {
    age:        _cachedAge,
    ageGroup:   _cachedAgeGroup,
    gender:     _cachedUser?.gender || null,
    silhouette: _cachedUser?.silhouette || null,
    heightCm:   _cachedUser?.height_cm || null,
    weightKg:   _cachedUser?.weight_kg || null,
    sizes:      _cachedUser?.clothing_sizes || {},
  };
}

export default {
  setCachedUser,
  getCachedUser,
  getCachedUserAge,
  getCachedUserAgeGroup,
  getCachedUserGender,
  getCachedUserSilhouette,
  getCachedUserHeight,
  getCachedUserWeight,
  getCachedUserSizes,
  getCachedUserContext,
};
